import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { identity, equals } from 'ramda';

import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
} from '@mui/material';

import Downwards from '../../icons/Downwards';
import Upwards from '../../icons/Upwards';
import { View } from '../../Base';

const Th = ({
  data,
  classes,
  Header,
  accessor,
  sortByAccessor,
  children,
  setOrder,
}) => {
  const setOwnOrder = useCallback(
    () => setOrder(sortByAccessor || accessor),
    [accessor, setOrder, sortByAccessor]
  );

  return (
    <TableCell
      size="small"
      padding="none"
      className={classes.headerCell}
      onClick={setOwnOrder}
    >
      <View className={classes.headerCellContainer}>
        {!!Header && <Header data={data} />}
        <span className={classes.sortArrow}>{children}</span>
      </View>
    </TableCell>
  );
};

// eslint-disable-next-line react/display-name
const TableBodyRow = memo(
  ({
    rowData,
    idx,
    classes,
    isParcelDetails,
    handleRowClick,
    isStopList,
    children,
    testId,
  }) => {
    const handleClick = useCallback(
      (event) => {
        handleRowClick(rowData, idx, event);
      },
      [handleRowClick, idx, rowData]
    );

    return (
      <TableRow
        className={clsx(
          isParcelDetails && {
            [classes.greyRow]: !(idx % 2),
          },
          isStopList && {
            [classes.selectedRow]: rowData.selected,
          }
        )}
        onClick={handleClick}
        data-testid={testId}
      >
        {children}
      </TableRow>
    );
  },
  (prevProps, nextProps) =>
    equals(prevProps.rowData, nextProps.rowData) &&
    prevProps.columns === nextProps.columns
);

// eslint-disable-next-line react/display-name
const RowCell = memo(
  ({ Cell, accessor, columnConfig, row, cellClassName }) => {
    const { width } = columnConfig;

    return (
      <TableCell
        key={`td_${accessor}`}
        data-testid={`td_${accessor}`}
        size="small"
        padding="none"
        className={cellClassName}
        style={{ maxWidth: width, minWidth: width }}
      >
        <Cell rowData={row} value={row[accessor]} />
      </TableCell>
    );
  },
  (prevProps, nextProps) =>
    equals(
      prevProps.row[prevProps.accessor],
      nextProps.row[nextProps.accessor]
    ) && prevProps.Cell === nextProps.Cell
);

const CustomTable = ({
  columns,
  data,
  isStopList,
  isParcelDetails,
  handleRowClick = identity,
  classes,
  order,
  isAscending,
  setOrder = identity,
  stickyHeader,
}) => (
  <View className={classes.tableWrap}>
    <TableContainer classes={{ root: classes.table }}>
      <Table stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              if (!column.columnConfig) {
                return null;
              }
              const orderAccessor = column.sortByAccessor || column.accessor;
              return (
                <Th
                  key={`thead_${column.accessor}`}
                  setOrder={setOrder}
                  data={data}
                  classes={classes}
                  {...column}
                >
                  {order === orderAccessor &&
                    (isAscending ? <Downwards /> : <Upwards />)}
                </Th>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableBodyRow
              key={`tr_${row.key || idx}`}
              testId={`tr_${row.key || idx}`}
              rowData={row}
              idx={idx}
              classes={classes}
              isParcelDetails={isParcelDetails}
              handleRowClick={handleRowClick}
              isStopList={isStopList}
              columns={columns}
            >
              {columns.map((props, i) => {
                if (!props.columnConfig) {
                  return null;
                }

                return (
                  <RowCell
                    {...props}
                    key={`row_cel_${row.key || idx}_${i}`}
                    row={row}
                    cellClassName={classes.bodyCell}
                  />
                );
              })}
            </TableBodyRow>
          ))}
        </TableBody>
        {isStopList && (
          <TableFooter>
            <TableRow>
              {columns.map(({ Footer, accessor, columnConfig }) => {
                if (!columnConfig) {
                  return null;
                }

                return (
                  <TableCell
                    key={`tfoot_${accessor}`}
                    size="small"
                    padding="none"
                    className={clsx(classes.bodyCell, classes.footerCell)}
                  >
                    {!!Footer && <Footer />}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  </View>
);

CustomTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  isStopList: PropTypes.bool,
  isParcelDetails: PropTypes.bool,
  handleRowClick: PropTypes.func,
};

export default memo(CustomTable, (prevProps, nextProps) =>
  equals(prevProps.data, nextProps.data)
);
